/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { dwService } from 'amb/services/dw.service';

/**
 * Einstein recommendation directive
 */
export const recommendationDirective = {
  /**
   * Cptions container
   */
  options: {},

  /**
   * @type {Map<string, {element: Element, options: object}}
   */
  container: new Map(),

  /**
   *
   * Validates and return the `CQuotient` namespace provided by the commerce cloud platform
   *
   * @returns {object} The `CQuotient` object or `null`
   */
  get einsteinUtils() {
    const isReady = window.CQuotient && ['getCQUserId', 'getCQCookieId'].every(method => {
      return typeof window.CQuotient[method] === 'function';
    });

    return isReady ? window.CQuotient : null;
  },

  /**
   * Initialize plugin for the given element or elements which match the selector
   *
   * @param {Element} element HTML DOM element
   * @param {object}      options Additional plugin options
   * @param {string}      options.recommender
   * @param {object}      [options.anchor]
   * @param {object}      [options.displays]
   * @param {number}      [options.limit]
   */
  init(element, options = {}) {
    element.classList.add('loading');
    this.container.set(element.ambid, { element, options });

    if (this.einsteinUtils) {
      this.loadRecommendations(element.ambid);
    } else {
      window.addEventListener('load', event => this.loadRecommendations(element.ambid), { once: true });
    }
  },

  /**
   * Destroy plugin for the given element
   *
   * @param {Element} element HTML DOM element
   */
  destroy(element) {
    this.container.delete(element.ambid);
  },

  /**
   * Load the recommended tiles and render carousel.
   *
   * @param {string}   ambid        Unique element ID
   * @param {object[]} recoProducts List of recommended products
   */
  loadRecommendationTiles(ambid, recoProducts) {
    const { element, options } = this.container.get(ambid);
    const { displays, limit, recommender } = options || {};

    const products = recoProducts.map(recoProduct => recoProduct.id);
    const url = new URL(dwService.url('Product-Recommendation'));

    url.searchParams.append('payload', JSON.stringify({ products, displays, limit, recommender }));

    fetch(url)
      .then(response => response.text())
      .then(body => {
        const isEmpty = !body.trim();
        const template = element.querySelector(`template[name="${isEmpty ? 'empty' : 'carousel'}"]`);

        element.classList.toggle('empty', isEmpty);

        if (template) {
          const content = template.content.firstElementChild.cloneNode(true);

          if (!isEmpty) {
            content.innerHTML = body;
          }

          element.appendChild(content);
        }
      })
      .catch(error => {
        element.classList.add('error');
        console.error(error);
      })
      .finally(() => {
        element.classList.remove('loading');
        element.classList.add('loaded');
      })
    ;
  },

  /**
   * Load the recommended products
  *
  * @param {string} ambid Unique element ID
  */
  loadRecommendations(ambid) {
    const { options } = this.container.get(ambid);

    const einsteinUtils = this.einsteinUtils;
    const recommenderName = options.recommender;
    const userId = einsteinUtils.getCQUserId();
    const cookieId = einsteinUtils.getCQCookieId();
    const parameters = { userId, cookieId, ccver: '1.01' };

    const callback = res => this.loadRecommendationTiles(ambid, res[recommenderName].recs);

    if (options.anchor) {
      parameters.anchors = [options.anchor];
    }

    if (typeof einsteinUtils.getRecs === 'function') {
      einsteinUtils.getRecs(einsteinUtils.clientId, recommenderName, parameters, callback);
    } else {
      einsteinUtils.widgets = einsteinUtils.widgets || [];
      einsteinUtils.widgets.push({ ambid, recommenderName, parameters, callback });
    }
  }
};
